import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  listCompanies,
  cropUnitsByCompanyID,
} from "graphql/queries";

const CropTypeForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [form, setForm] = useState(initialValues || {});
  const [companies, setCompanies] = useState({ list: [], loading: false });
  const [cropUnits, setCropUnits] = useState({ list: [], loading: false });
  const [errors, setErrors] = useState({
    name: "",
    companyID: "",
    cropUnitID: "",
  });

  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
    cropUnitID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(form).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;
    if (checkIncorrectValue("cropUnitID", cropUnits.list)) return;

    if (!hasErrors) onSubmit(form);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === form[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    if (name === "cropUnitID") {
      if (form.companyID)
        fetchCropUnits({ companyID: form.companyID, name: value });
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    if (name === "companyID") {
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
        cropUnitID: "",
      }));
      fetchCropUnits({ companyID: value });
      checkErrors(name, value);
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      setCropUnits({ list: [], loading: false });
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
        cropUnitID: "",
      }));
      return;
    }
    if (name === "cropUnitID") {
      if (form.companyID) fetchCropUnits({ companyID: form.companyID, name: "" });
      setForm((prevSt) => ({ ...prevSt, [name]: value }));
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({ name: "", companyID: "", cropUnitID: "" });
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    fetchCropUnits({
      companyID: initialValues?.companyID,
      id: initialValues?.cropUnitID,
    });
  };

  async function fetchCropUnits({
    companyID = "",
    id = "",
    name = "",
  }) {
    try {
      setCropUnits({ list: [], loading: true });
      if (!companyID) {
        setCropUnits({ list: [], loading: false });
        // Clean list if no company
        return;
      }
      let variables = { companyID };
      if (name) variables.filter = { name: { contains: name } };
      if (id) variables.filter = { id: { eq: id } };
      variables.filter = {
        ...variables.filter,
        deletedAt: { attributeExists: false }
      };

      const cropUnitsData = await API.graphql(
        graphqlOperation(cropUnitsByCompanyID, variables)
      );
      const cropUnitsItems = cropUnitsData.data.cropUnitsByCompanyID.items;
      setCropUnits((prev) => ({ ...prev, list: cropUnitsItems }));

    } catch (err) {
      console.log(err)
      toast.error(strings.responses.errorFetchCropUnits);
    } finally {
      setCropUnits((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me?.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me?.managedCompanies?.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [{ id: { contains: id } }, { name: { contains: name } }],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    fetchCropUnits({
      companyID: initialValues?.companyID,
      id: initialValues?.cropUnitID,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={form.name || ""}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          />
          <Flex direction="column" gap="xs">
            <Label htmlFor="companyID">
              <LabelRequired text={strings.fields.company.label} />
            </Label>
            <Autocomplete
              id="companyID"
              name="companyID"
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.list.find(
                  (companyRec) => companyRec.id === form.companyID
                )?.name ??
                form.companyID ??
                ""
              }
              options={companies.list.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companies.loading}
              isDisabled={type === 'update'}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors.companyID}
              hasError={!!errors.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
        </Grid>

        <Grid
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <Flex direction="column" gap="xs">
            <Label htmlFor="cropUnitID">
              <LabelRequired text={strings.fields.cropUnit.label} />
            </Label>
            <Autocomplete
              id="cropUnitID"
              name="cropUnitID"
              disabled={!form.companyID}
              isRequired
              placeholder={strings.fields.cropUnit.placeholder}
              value={
                cropUnits.list.find((f) => f.id === form.cropUnitID)?.name ??
                form.cropUnitID ??
                ""
              }
              options={cropUnits.list.map((rec) => ({
                id: rec.id,
                label: rec.name,
              }))}
              isLoading={cropUnits.loading}
              onClear={() => onClearSelect("cropUnitID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("cropUnitID", id)}
              errorMessage={errors.cropUnitID}
              hasError={!!errors.cropUnitID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
        </Grid>

        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          />
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          />
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: { label: "Nombre" },
      company: { label: "Empresa", placeholder: "Buscar empresa..." },
      cropUnit: { label: "Unidad de Cultivo", placeholder: "Buscar unidad..." },
      buttons: { create: "Crear", save: "Guardar", reset: "Restablecer" },
    },
    validations: { incorrectValue: "Valor incorrecto" },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchCropUnits: "Error al consultar unidades de cultivo"
    },
  },
});

export default CropTypeForm;
